import { Box, FormControl, MenuItem, Select, Stack, Typography, useTheme } from "@mui/material";
import { delftBlue } from "@/theme/colors";

const ExpandMoreIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.59 8.29498L12 12.875L7.41 8.29498L6 9.70498L12 15.705L18 9.70498L16.59 8.29498Z"
      fill="var(--Colour-Neutrals-Black, black)"
    />
  </svg>
);

const ActivitiesPagination = ({
  page = 1,
  limit = 10,
  total = 114,
  onPageChange,
  onLimitChange,
}) => {
  const theme = useTheme();
  const totalPages = Math.ceil(total / limit);

  return (
    <Box
      sx={{
        alignSelf: "stretch",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 2, sm: 0 },
      }}
    >
      <Stack direction="row" spacing={{ xs: 1, sm: 1.25 }} alignItems="center" flexWrap="wrap">
        <Typography
          sx={{
            color: theme.palette.neutral[900],
            fontSize: { xs: 14, sm: 15, md: 16 },
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 400,
            whiteSpace: "nowrap",
          }}
        >
          Rows per page
        </Typography>
        <FormControl
          sx={{
            borderRadius: 2,
          }}
        >
          <Select
            value={limit}
            onChange={(e) => onLimitChange && onLimitChange(e.target.value)}
            sx={{
              paddingLeft: { xs: 0.75, sm: 1 },
              paddingRight: { xs: 0.75, sm: 1 },
              paddingTop: { xs: 0.5, sm: 0.5 },
              paddingBottom: { xs: 0.5, sm: 0.5 },
              background: delftBlue[100],
              borderRadius: 2,
              outline: `1px ${delftBlue[400]} solid`,
              outlineOffset: "-1px",
              minHeight: { xs: 36, sm: "auto" },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-select": {
                paddingRight: "32px !important",
                color: theme.palette.neutral[900],
                fontSize: { xs: 14, sm: 15, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.6,
                display: "flex",
                alignItems: "center",
              },
            }}
            IconComponent={() => (
              <Box
                sx={{
                  marginRight: 1,
                  pointerEvents: "none",
                }}
              >
                <ExpandMoreIcon />
              </Box>
            )}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <Typography
          sx={{
            color: theme.palette.neutral[900],
            fontSize: { xs: 14, sm: 15, md: 16 },
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 400,
            whiteSpace: "nowrap",
          }}
        >
          of {total} rows
        </Typography>
      </Stack>

      <Stack 
        direction="row" 
        spacing={{ xs: 0.25, sm: 0.5 }} 
        alignItems="center"
        sx={{
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: { xs: 0.5, sm: 0 },
        }}
      >
        <Box
          onClick={() => page > 1 && onPageChange && onPageChange(page - 1)}
          sx={{
            paddingLeft: { xs: 1.5, sm: 2 },
            paddingRight: { xs: 1.5, sm: 2 },
            paddingTop: { xs: 0.5, sm: 0.5 },
            paddingBottom: { xs: 0.5, sm: 0.5 },
            background: page === 1 ? delftBlue[50] : delftBlue[50],
            borderRadius: 2,
            cursor: page === 1 ? "not-allowed" : "pointer",
            opacity: page === 1 ? 0.5 : 1,
            touchAction: "manipulation",
            minHeight: { xs: 36, sm: "auto" },
            display: "flex",
            alignItems: "center",
            "&:active": {
              opacity: page === 1 ? 0.5 : 0.7,
            },
          }}
        >
          <Typography
            sx={{
              color: theme.palette.neutral[300],
              fontSize: { xs: 14, sm: 15, md: 16 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            Previous
          </Typography>
        </Box>

        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (page <= 3) {
            pageNum = i + 1;
          } else if (page >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = page - 2 + i;
          }

          if (i === 3 && totalPages > 5 && page < totalPages - 2) {
            return (
              <Box
                key="ellipsis"
                sx={{
                  paddingLeft: { xs: 1, sm: 2 },
                  paddingRight: { xs: 1, sm: 2 },
                  paddingTop: 0.5,
                  paddingBottom: 0.5,
                  borderRadius: 0.25,
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.neutral[900],
                    fontSize: { xs: 14, sm: 15, md: 16 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  ...
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              key={pageNum}
              onClick={() => onPageChange && onPageChange(pageNum)}
              sx={{
                paddingLeft: { xs: 1, sm: 2 },
                paddingRight: { xs: 1, sm: 2 },
                paddingTop: 0.5,
                paddingBottom: 0.5,
                background: page === pageNum ? delftBlue[100] : "transparent",
                borderRadius: page === pageNum ? 2 : 0.25,
                outline: page === pageNum ? `1px ${delftBlue[400]} solid` : "none",
                outlineOffset: page === pageNum ? "-1px" : 0,
                cursor: "pointer",
                touchAction: "manipulation",
                minWidth: { xs: 32, sm: "auto" },
                minHeight: { xs: 32, sm: "auto" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  background: page === pageNum ? delftBlue[100] : theme.palette.neutral[50],
                },
                "&:active": {
                  background: page === pageNum ? delftBlue[200] : theme.palette.neutral[100],
                },
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.neutral[900],
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                {pageNum}
              </Typography>
            </Box>
          );
        })}

        <Box
          onClick={() => page < totalPages && onPageChange && onPageChange(page + 1)}
          sx={{
            paddingLeft: { xs: 1.5, sm: 2 },
            paddingRight: { xs: 1.5, sm: 2 },
            paddingTop: { xs: 0.5, sm: 0.5 },
            paddingBottom: { xs: 0.5, sm: 0.5 },
            background: delftBlue[100],
            borderRadius: 2,
            outline: `1px ${delftBlue[400]} solid`,
            outlineOffset: "-1px",
            cursor: page === totalPages ? "not-allowed" : "pointer",
            opacity: page === totalPages ? 0.5 : 1,
            touchAction: "manipulation",
            minHeight: { xs: 36, sm: "auto" },
            display: "flex",
            alignItems: "center",
            "&:active": {
              opacity: page === totalPages ? 0.5 : 0.7,
            },
          }}
        >
          <Typography
            sx={{
              color: theme.palette.neutral[900],
              fontSize: { xs: 14, sm: 15, md: 16 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            Next
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default ActivitiesPagination;



